import styled from 'styled-components';

import NotificationStyled from '../../../../../components/Notification';
import StickyNav from '../../../../../components/StickyNav';
import { IconInfoCircle as InfoCircle } from '../../../../../core';
import UseCasePalettes from '../../../components/UseCasePalettes';

interface Props {
  className?: string;
}

const UseCaseTab = ({ className = '' }: Props) => (
  <div className={className}>
    <div className="tab-content">
      <div className="definition">
        <h2 className="title" id="definition">
          Definition
        </h2>
        <p>
          Circle packing is a data visualization method that displays hierarchical data using
          non-overlapping circles. Each circle represents a category or subcategory, with the size
          of the circle corresponding to a particular metric, such as value, quantity, or
          importance. The circles are nested within one another, with larger circles representing
          higher-level categories and smaller circles representing subcategories. This technique
          provides a visually appealing way to understand proportions, relationships, and data
          distribution, making it ideal for visualizing complex hierarchical structures, such as
          organizational charts, budget breakdowns, or resource allocation.
        </p>
      </div>
      <div className="usage">
        <h2 className="title" id="usage">
          Usage
        </h2>
        <p className="subtitle">
          Circle packing charts are an engaging way to represent hierarchical and relational data
          through nested circles. Here are any key uses:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Displaying Hierarchical Structures:</strong> They are excellent for visualizing
            relationships within hierarchical datasets, such as organizational charts or file
            directory structures.
          </li>
          <li>
            <strong>Comparing Proportional Sizes:</strong> Circle packing emphasizes the relative
            sizes of categories, allowing viewers to easily identify dominant or minor groups.
          </li>
          <li>
            <strong>Maximizing Visual Appeal:</strong> The nested layout creates an intuitive and
            visually pleasing representation, ideal for simplifying complex data for broader
            audiences.
          </li>
        </ul>
      </div>
      <div className="limitations">
        <h2 className="title" id="limitations">
          Limitations
        </h2>
        <p className="subtitle">
          Circle packing charts are visually appealing for hierarchical data, but they come with
          significant limitations:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Inefficient Use of Space:</strong> Circles inherently leave gaps between them,
            resulting in inefficient space utilization. This can limit the chart’s ability to
            display large datasets effectively or make it harder to interpret precise relationships
            between elements.
          </li>
          <li>
            <strong>Difficulty with Size Comparison:</strong> Comparing the sizes of circles is less
            intuitive than comparing bars or rectangles. Subtle differences can be hard to discern,
            reducing the chart&apos;s effectiveness for detailed analysis.
          </li>
          <li>
            <strong>Limited Depth Representation:</strong> Displaying deeply nested hierarchies is
            challenging, as inner circles can become too small to provide meaningful insights. This
            restricts the chart&apos;s ability to convey complex data structures clearly.
          </li>
        </ul>
      </div>
      <div className="colors">
        <h2 className="title" id="colors">
          Colors
        </h2>
        <p>
          Color selection in circle packing charts plays a vital role in enhancing data
          interpretation. Distinct colors can represent different categories or hierarchical levels,
          making relationships and groupings easily recognizable. Gradient color schemes can be used
          to indicate value or magnitude, with gradual transitions reflecting intensity. Consistent
          and thoughtful color choices prevent visual overload and ensure the chart remains
          intuitive and engaging for the audience.
        </p>
        <NotificationStyled
          icon={<InfoCircle />}
          title="For in-depth information on this topic, please refer to the Colors page."
          color="blue"
          className="info-notification"
        />
        <UseCasePalettes palettes={['qualitative']} />
      </div>

      <div className="challenges">
        <h2 className="title">Challenges</h2>
        <p>
          Circle packing charts provide an intuitive way to display hierarchical data, but they
          present unique challenges that can impact their effectiveness:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Space Utilization and Readability:</strong> The circular layout often results in
            inefficient space usage, especially for deeply nested hierarchies. Overlapping or small
            circles can reduce clarity, making it hard to interpret detailed information.
          </li>
          <li>
            <strong>Comparison Difficulty:</strong> Comparing the size of circles can be
            challenging, particularly when they are of similar dimensions or irregularly arranged.
            This may lead to misinterpretations of relative importance or values.
          </li>
          <li>
            <strong>Complexity in Large Data Sets:</strong> When visualizing large or deeply nested
            datasets, circle packing charts can become overcrowded, making it difficult to
            understand the hierarchical structure and relationships between elements.
          </li>
        </ul>
      </div>
    </div>
    <div>
      <StickyNav title="Contents" />
    </div>
  </div>
);

const StyledUseCaseTab = styled(UseCaseTab)`
  display: flex;
  gap: 2.5rem;
  justify-content: space-between;

  > .tab-content {
    width: 100%;
    .usage,
    .definition,
    .limitations,
    .challenges {
      max-width: 700px;
    }
    > .colors {
      > .info-notification {
        margin-top: 24px;
        margin-bottom: 40px;
      }

      > .palette {
        > .palette-title {
          color: ${({ theme }) => theme.neutralText};
          font-size: ${({ theme }) => theme.fontSizes.base};
          line-height: ${({ theme }) => theme.lineHeights.base};
          margin-bottom: 4px;
        }
      }

      .color-palette {
        margin-top: 12px;
      }
    }

    > .challenges {
      > .ordered-list-badge-num {
        margin-top: 16px;
      }
    }

    .title {
      color: ${({ theme }) => theme.neutralText};
      font-weight: 600;
      font-size: ${({ theme }) => theme.fontSizes.lg};
      line-height: ${({ theme }) => theme.lineHeights.lg};
      margin-bottom: 8px;
      margin-top: 40px;
    }

    p {
      color: ${({ theme }) => theme.neutralTextWeak};
      font-size: ${({ theme }) => theme.fontSizes.sm};
      line-height: ${({ theme }) => theme.lineHeights.sm};
      font-weight: 400;
      max-width: 700px;
    }

    .subtitle {
      margin-bottom: 16px;
    }

    // styles for list with badge
    .ordered-list-badge-num {
      font-size: 0.875rem;
      line-height: 1.4285714286;
      margin-left: 29px;
      counter-reset: num;
      list-style: none;
      color: ${({ theme }) => theme.neutralTextWeak};

      li {
        position: relative;
        margin-bottom: 16px;

        &::before {
          content: counter(num);
          counter-increment: num;
          position: absolute;
          left: -1.75rem;
          top: 0;
          width: 1.25rem;
          height: 1.25rem;
          border-radius: 0.1875rem;
          text-align: center;
          font-size: 0.6875rem;
          line-height: 1.8;
          color: ${({ theme }) => theme.primaryText};
          background-color: ${({ theme }) => theme.primaryBackgroundActive};
        }
      }
    }
  }
`;

export default StyledUseCaseTab;
